// useExtensionBridge: orchestrates ping/handshake with the Terum Chrome extension.
//
// Flow:
//   1. Mount: send window-postMessage `terum-extension-ping`.
//   2. content-announce.js (in the extension) responds with `terum-extension-ready`.
//   3. We fetch a single-use handoff token from /api/onboarding/extension (Bearer).
//   4. We post `terum-handshake-request` with the user's Supabase tokens + handoff token.
//   5. content-announce.js relays to background; background ACKs the handoff with the BFF and persists tokens.
//   6. We receive `terum-handshake-response` matched by requestId.
//
// Bug guards:
//   - Strict event.origin === window.location.origin on every inbound message.
//   - 3s timeout flips status to 'not_installed' if no extension responds.
//   - requestId (UUID) on every handshake-request; stale responses ignored.
//   - Listener + timer cleanup on unmount.
//   - Idempotent: only one in-flight handshake per ready event.

import { useEffect, useState, useRef, useCallback } from "react";
import { supabase, getAccessToken } from "../lib/auth";

const DETECTION_TIMEOUT_MS = 3000;
const HANDSHAKE_TIMEOUT_MS = 10000;

type Status =
  | "detecting"
  | "not_installed"
  | "connecting"
  | "connected"
  | "error";

export interface ExtensionBridgeState {
  status: Status;
  extensionId: string | null;
  error: string | null;
  retry: () => void;
}

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Fallback for older environments / test harnesses.
  return `req-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useExtensionBridge(): ExtensionBridgeState {
  const [status, setStatus] = useState<Status>("detecting");
  const [extensionId, setExtensionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);

  // requestId of the in-flight handshake; survives re-renders and lets us
  // discard stale handshake-response messages (multi-tab, retries).
  const inFlightRequestId = useRef<string | null>(null);

  const retry = useCallback(() => {
    setStatus("detecting");
    setExtensionId(null);
    setError(null);
    inFlightRequestId.current = null;
    setTrigger((t) => t + 1);
  }, []);

  useEffect(() => {
    let detectTimer: ReturnType<typeof setTimeout> | null = null;
    let handshakeTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    // Per-effect-run guard against firing handshake twice for the same extId.
    // Local to this effect (not a ref) so React 18 StrictMode unmount/remount
    // gives the new mount a fresh state — a persistent ref would block the
    // second mount from ever handshaking. Re-firing on remount is safe because
    // the BFF upserts the connection idempotently.
    const handshakeFiredFor = new Set<string>();

    const cleanup = () => {
      cancelled = true;
      if (detectTimer) clearTimeout(detectTimer);
      if (handshakeTimer) clearTimeout(handshakeTimer);
      window.removeEventListener("message", handleMessage);
    };

    async function runHandshake(extId: string) {
      if (handshakeFiredFor.has(extId)) return;
      handshakeFiredFor.add(extId);

      setStatus("connecting");
      setError(null);

      try {
        // Refresh the session so the access token we send isn't already expired.
        await supabase.auth.getSession();
        const accessToken = await getAccessToken();
        if (!accessToken) {
          if (!cancelled) {
            setStatus("error");
            setError("Not signed in.");
          }
          return;
        }

        // Step 3: fetch a handoff token from BFF (Bearer-authenticated).
        const apiBase = import.meta.env.VITE_API_BASE_URL || "";
        const handoffRes = await fetch(`${apiBase}/api/onboarding/extension`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!handoffRes.ok) {
          if (!cancelled) {
            setStatus("error");
            setError(
              handoffRes.status === 401
                ? "Session expired. Please sign in again."
                : `Could not start handshake (${handoffRes.status}).`
            );
          }
          return;
        }
        const handoff = await handoffRes.json();
        if (!handoff?.handoffToken) {
          if (!cancelled) {
            setStatus("error");
            setError("Server returned an invalid handoff response.");
          }
          return;
        }

        const { data: { session } } = await supabase.auth.getSession();
        const refreshToken = session?.refresh_token;

        // Step 4: postMessage handshake-request with credentials + handoff.
        const requestId = uuid();
        inFlightRequestId.current = requestId;

        // Hard timeout in case the extension never responds.
        handshakeTimer = setTimeout(() => {
          if (cancelled) return;
          if (inFlightRequestId.current === requestId) {
            inFlightRequestId.current = null;
            setStatus("error");
            setError("Extension did not respond. Try refreshing the page.");
          }
        }, HANDSHAKE_TIMEOUT_MS);

        window.postMessage(
          {
            type: "terum-handshake-request",
            requestId,
            accessToken,
            refreshToken,
            supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
            supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            apiBaseUrl: apiBase || window.location.origin,
            handoffToken: handoff.handoffToken,
          },
          window.location.origin
        );
      } catch (e) {
        if (!cancelled) {
          setStatus("error");
          setError(e instanceof Error ? e.message : "Handshake failed.");
        }
      }
    }

    function handleMessage(event: MessageEvent) {
      // Strict origin check — only trust messages from this same window.
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || typeof data !== "object") return;

      if (data.type === "terum-extension-ready" && typeof data.extensionId === "string") {
        if (detectTimer) {
          clearTimeout(detectTimer);
          detectTimer = null;
        }
        setExtensionId(data.extensionId);
        // runHandshake itself transitions status: detecting/not_installed → connecting.
        void runHandshake(data.extensionId);
        return;
      }

      if (data.type === "terum-handshake-response") {
        // Ignore responses for other in-flight requests (multi-tab, retries).
        if (data.requestId !== inFlightRequestId.current) return;
        inFlightRequestId.current = null;
        if (handshakeTimer) {
          clearTimeout(handshakeTimer);
          handshakeTimer = null;
        }
        if (data.ok) {
          setStatus("connected");
          setError(null);
        } else {
          setStatus("error");
          setError(typeof data.error === "string" ? data.error : "Handshake rejected.");
        }
        return;
      }

      if (data.type === "terum-extension-error") {
        // Best-effort surfacing of extension-side errors from the bridge.
        if (typeof data.error === "string") {
          setStatus("error");
          setError(data.error);
        }
      }
    }

    window.addEventListener("message", handleMessage);

    // Kick off detection: ping the extension after the listener is attached.
    window.postMessage(
      { type: "terum-extension-ping" },
      window.location.origin
    );

    detectTimer = setTimeout(() => {
      if (cancelled) return;
      // No 'ready' message arrived — extension is not installed (or content
      // script blocked). Note: a later 'ready' will still trigger handshake.
      setStatus((current) => (current === "detecting" ? "not_installed" : current));
    }, DETECTION_TIMEOUT_MS);

    return cleanup;
    // `trigger` re-runs the effect on retry(); status intentionally not
    // depended on so we don't re-bind listeners every state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  return { status, extensionId, error, retry };
}
